/**
 * InterestRatesSection Component
 * 3 horizontal cards (one per term: 4, 8, 12 weeks) with:
 * - Percentage input (operator types 55, stored as 0.55)
 * - Auto-calculated daily rate
 * - Example weekly payment for a reference amount
 * - Info modal explaining French amortization + ACT/360
 */

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Save, Info, Calendar, Percent } from 'lucide-react';
import { ConfirmSaveModal } from '../ConfirmSaveModal';
import type { InterestRateItem } from '@/types/policy.types';
import type { OpeningFeeDraft } from '@/features/policies/hooks/usePolicies';

interface InterestRatesSectionProps {
  data: InterestRateItem[] | null;
  feeData: OpeningFeeDraft | null;
  canEdit: boolean;
  onSave: (data: InterestRateItem[]) => void;
  onSaveFee: (data: OpeningFeeDraft) => void;
  isSaving: boolean;
  isSavingFee: boolean;
}

const REFERENCE_AMOUNT = 5000; // MXN for example calculation

const TERM_THEMES: Record<number, { color: string; bg: string; label: string; description: string }> = {
  4: {
    color: '#10B981', bg: 'rgba(16,185,129,0.12)',
    label: '4 semanas', description: 'Plazo corto. Cuotas mas altas pero menos intereses totales.',
  },
  8: {
    color: '#F59E0B', bg: 'rgba(245,158,11,0.12)',
    label: '8 semanas', description: 'Plazo medio. Balance entre cuota accesible e intereses.',
  },
  12: {
    color: '#3B82F6', bg: 'rgba(59,130,246,0.12)',
    label: '12 semanas', description: 'Plazo largo. Cuotas mas bajas pero mas intereses totales.',
  },
};

const DEFAULT_TERM_THEME = { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', label: '?', description: '' };

const FEE_THEME = { color: '#8B5CF6', bg: 'rgba(139,92,246,0.12)' };

// IVA y beneficiario son metadatos display-only de esta seccion. El IVA real
// vive en split_config.iva_rate y el beneficiario en split (UNE-Credito).
const FEE_IVA_DISPLAY = 0.16;
const FEE_BENEFICIARY_DISPLAY = 'UNE';

const DEFAULT_FEE: OpeningFeeDraft = { rate: 0.04, is_financed: true };

/** French amortization: weekly payment for P pesos, N weeks, annual rate R (ACT/360) */
function calcWeeklyPayment(principal: number, weeks: number, annualRate: number): number {
  const days = weeks * 7;
  const periodRate = annualRate * days / 360;
  const weeklyRate = periodRate / weeks;
  if (weeklyRate === 0) return principal / weeks;
  return principal * weeklyRate * Math.pow(1 + weeklyRate, weeks) / (Math.pow(1 + weeklyRate, weeks) - 1);
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n);

export function InterestRatesSection({ data, feeData, canEdit, onSave, onSaveFee, isSaving, isSavingFee }: InterestRatesSectionProps) {
  const [draft, setDraft] = useState<InterestRateItem[] | null>(null);
  const [feeDraft, setFeeDraft] = useState<OpeningFeeDraft | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showMethodInfo, setShowMethodInfo] = useState(false);

  const sorted = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => a.term_weeks - b.term_weeks);
  }, [data]);

  const feeSource: OpeningFeeDraft = feeData ?? DEFAULT_FEE;
  const currentFee: OpeningFeeDraft = feeDraft ?? feeSource;

  if (sorted.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Sin tasas de interes configuradas.
      </p>
    );
  }

  const current = draft ?? sorted;
  const isDirty = draft !== null || feeDraft !== null;

  function updateRate(index: number, pctValue: number) {
    const base = draft ?? structuredClone(sorted);
    const updated = [...base];
    const item = updated[index];
    if (item) {
      updated[index] = { ...item, annual_rate: pctValue / 100 };
      setDraft(updated);
    }
  }

  function updateFeeRate(pctValue: number) {
    const base = feeDraft ?? structuredClone(feeSource);
    setFeeDraft({ ...base, rate: pctValue / 100 });
  }

  function updateFeeFinanced(value: boolean) {
    const base = feeDraft ?? structuredClone(feeSource);
    setFeeDraft({ ...base, is_financed: value });
  }

  function handleConfirm() {
    if (draft) onSave(draft);
    if (feeDraft) onSaveFee(feeDraft);
    setShowConfirm(false);
    setDraft(null);
    setFeeDraft(null);
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Tasa anual por plazo. Se aplica metodo frances con convencion ACT/360.
        </p>
        <button
          type="button"
          onClick={() => setShowMethodInfo(true)}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-sky-400 transition-colors hover:bg-sky-500/15"
        >
          <Info size={14} />
          Metodo de calculo
        </button>
      </div>

      {/* Rate cards */}
      {(() => {
        // Pre-calculate all costs to detect non-progressive patterns
        const costs = current.map((rate) => {
          const weeklyPayment = calcWeeklyPayment(REFERENCE_AMOUNT, rate.term_weeks, rate.annual_rate);
          const totalPayment = weeklyPayment * rate.term_weeks;
          const totalInterest = totalPayment - REFERENCE_AMOUNT;
          const costPer1000PerWeek = (totalInterest / REFERENCE_AMOUNT / rate.term_weeks) * 1000;
          return { weeklyPayment, totalPayment, totalInterest, costPer1000PerWeek };
        });

        // Check if cost/week is progressive (each term should cost more per week than the previous)
        const nonProgressiveFlags = costs.map((c, i) => {
          if (i === 0) return false;
          return c.costPer1000PerWeek < costs[i - 1]!.costPer1000PerWeek;
        });
        const hasNonProgressive = nonProgressiveFlags.some(Boolean);

        return (<>
      {hasNonProgressive && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2">
          <span className="text-amber-400">⚠</span>
          <p className="text-xs text-amber-400">
            El costo semanal por $1,000 no es progresivo — un plazo mas corto cuesta mas por semana que uno mas largo. Revisa si las tasas estan correctamente calibradas.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {current.map((rate, index) => {
          const theme = TERM_THEMES[rate.term_weeks] ?? DEFAULT_TERM_THEME;
          const pctDisplay = (rate.annual_rate * 100);
          const dailyRate = rate.annual_rate / 360;
          const { weeklyPayment, totalPayment, totalInterest, costPer1000PerWeek } = costs[index]!;
          const isNonProgressive = nonProgressiveFlags[index];

          return (
            <div
              key={rate.sk}
              className={`rounded-lg border bg-card p-4 border-l-[3px] ${!rate.is_active ? 'opacity-40' : ''}`}
              style={{ borderLeftColor: theme.color }}
            >
              {/* Term header */}
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar size={16} style={{ color: theme.color }} />
                  <span className="text-sm font-bold text-foreground">{theme.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span
                    className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: theme.bg, color: theme.color }}
                  >
                    FRENCH
                  </span>
                  <span
                    className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{ backgroundColor: theme.bg, color: theme.color }}
                  >
                    ACT/360
                  </span>
                </div>
              </div>

              {/* Description */}
              <p className="mb-3 text-[11px] text-muted-foreground">{theme.description}</p>

              {/* Annual rate — big editable number */}
              <div className="mb-3">
                <span className="text-[11px] text-muted-foreground">Tasa anual</span>
                <div className="mt-0.5 flex items-baseline gap-1">
                  {canEdit ? (
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="999"
                      value={parseFloat(pctDisplay.toFixed(1))}
                      onChange={(e) => updateRate(index, Number(e.target.value))}
                      className="h-10 w-20 rounded-md border bg-muted/50 px-2 text-2xl font-bold text-center transition-colors focus:outline-none"
                      style={{ color: theme.color, borderColor: `${theme.color}40` }}
                    />
                  ) : (
                    <span className="text-3xl font-bold" style={{ color: theme.color }}>
                      {pctDisplay.toFixed(1)}
                    </span>
                  )}
                  <span className="text-lg font-semibold text-muted-foreground">%</span>
                </div>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  Tasa diaria: {(dailyRate * 100).toFixed(4)}%
                </p>
              </div>

              {/* Example */}
              <div className="rounded-md p-2.5" style={{ backgroundColor: `${theme.color}10` }}>
                <p className="mb-2 text-[10px] font-semibold" style={{ color: theme.color }}>
                  Ejemplo con {formatCurrency(REFERENCE_AMOUNT)}
                </p>

                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cuota semanal</span>
                    <span className="font-bold" style={{ color: theme.color }}>{formatCurrency(weeklyPayment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total a pagar</span>
                    <span className="font-semibold text-foreground">{formatCurrency(totalPayment)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Intereses totales</span>
                    <span className="font-semibold text-muted-foreground">{formatCurrency(totalInterest)}</span>
                  </div>
                </div>

                {/* Comparable metric */}
                <div
                  className="mt-2 flex items-center justify-between rounded-md border px-2 py-1.5"
                  style={{ borderColor: isNonProgressive ? 'rgba(245,158,11,0.4)' : `${theme.color}30` }}
                >
                  <span className="text-[10px] text-muted-foreground">Costo por $1,000 / semana</span>
                  <div className="flex items-center gap-1">
                    {isNonProgressive && <span className="text-xs text-amber-400">↓</span>}
                    <span className={`text-sm font-bold ${isNonProgressive ? 'text-amber-400' : ''}`} style={isNonProgressive ? undefined : { color: theme.color }}>
                      ${costPer1000PerWeek.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      </>);
      })()}

      {/* Opening Fee Card — full width */}
      <div
        className="rounded-lg border bg-card p-4 border-l-[3px]"
        style={{ borderLeftColor: FEE_THEME.color }}
      >
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Percent size={16} style={{ color: FEE_THEME.color }} />
            <span className="text-sm font-bold text-foreground">Comision de Apertura</span>
          </div>
          <span
            className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium"
            style={{ backgroundColor: FEE_THEME.bg, color: FEE_THEME.color }}
          >
            FLAT
          </span>
        </div>

        <p className="mb-4 text-[11px] text-muted-foreground">
          Se aplica sobre el monto solicitado. Independiente del plazo y nivel.
        </p>

        {/* Rate input + metadata */}
        <div className="mb-4 flex flex-wrap items-start gap-8">
          {/* Editable rate */}
          <div>
            <span className="text-[11px] text-muted-foreground">Comision</span>
            <div className="mt-0.5 flex items-baseline gap-1">
              {canEdit ? (
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="20"
                  value={parseFloat((currentFee.rate * 100).toFixed(1))}
                  onChange={(e) => updateFeeRate(Number(e.target.value))}
                  className="h-10 w-20 rounded-md border bg-muted/50 px-2 text-2xl font-bold text-center transition-colors focus:outline-none"
                  style={{ color: FEE_THEME.color, borderColor: `${FEE_THEME.color}40` }}
                />
              ) : (
                <span className="text-3xl font-bold" style={{ color: FEE_THEME.color }}>
                  {(currentFee.rate * 100).toFixed(1)}
                </span>
              )}
              <span className="text-lg font-semibold text-muted-foreground">%</span>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex gap-8 pt-1">
            <div>
              <span className="text-[10px] text-muted-foreground">Modalidad</span>
              {canEdit ? (
                <label className="mt-0.5 flex cursor-pointer items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={currentFee.is_financed}
                    onChange={(e) => updateFeeFinanced(e.target.checked)}
                    className="h-3.5 w-3.5 cursor-pointer accent-current"
                    style={{ color: FEE_THEME.color }}
                  />
                  <span className="text-xs font-medium text-foreground">
                    {currentFee.is_financed ? 'Financiada' : 'No financiada'}
                  </span>
                </label>
              ) : (
                <p className="text-xs font-medium text-foreground">
                  {currentFee.is_financed ? 'Financiada' : 'No financiada'}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground">
                {currentFee.is_financed ? 'Se suma al principal' : 'Se cobra por separado'}
              </p>
            </div>
            <div>
              <span className="text-[10px] text-muted-foreground">IVA</span>
              <p className="text-xs font-medium text-foreground">{(FEE_IVA_DISPLAY * 100).toFixed(0)}%</p>
              <p className="text-[10px] text-muted-foreground">Retenido por FiadoSA</p>
            </div>
            <div>
              <span className="text-[10px] text-muted-foreground">Beneficiario</span>
              <p className="text-xs font-medium text-foreground">{FEE_BENEFICIARY_DISPLAY}</p>
              <p className="text-[10px] text-muted-foreground">Recibe comision neta</p>
            </div>
          </div>
        </div>

        {/* Example */}
        {(() => {
          const feeAmount = REFERENCE_AMOUNT * currentFee.rate;
          const feeIva = feeAmount * FEE_IVA_DISPLAY;
          const financedPrincipal = currentFee.is_financed
            ? REFERENCE_AMOUNT + feeAmount + feeIva
            : REFERENCE_AMOUNT;

          return (
            <div className="rounded-md p-2.5" style={{ backgroundColor: `${FEE_THEME.color}10` }}>
              <p className="mb-2 text-[10px] font-semibold" style={{ color: FEE_THEME.color }}>
                Ejemplo con {formatCurrency(REFERENCE_AMOUNT)}
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs">
                <div className="flex gap-2">
                  <span className="text-muted-foreground">Comision</span>
                  <span className="font-semibold text-foreground">{formatCurrency(feeAmount)}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-muted-foreground">IVA comision</span>
                  <span className="font-semibold text-foreground">{formatCurrency(feeIva)}</span>
                </div>
                <div className="ml-auto flex items-center gap-2 rounded-md border px-2 py-1.5" style={{ borderColor: `${FEE_THEME.color}30` }}>
                  <span className="text-[10px] text-muted-foreground">Principal financiado</span>
                  <span className="text-sm font-bold" style={{ color: FEE_THEME.color }}>
                    {formatCurrency(financedPrincipal)}
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Save bar */}
      {isDirty && (
        <div className="sticky bottom-4 flex items-center justify-end gap-2 rounded-lg border bg-card p-2.5 shadow-lg">
          <span className="mr-auto text-xs text-muted-foreground">Cambios sin guardar</span>
          <Button variant="outline" size="sm" onClick={() => { setDraft(null); setFeeDraft(null); }}>
            Descartar
          </Button>
          <Button variant="primary" size="sm" leftIcon={<Save size={14} />} onClick={() => setShowConfirm(true)}>
            Guardar
          </Button>
        </div>
      )}

      {/* Method info modal */}
      <Modal
        isOpen={showMethodInfo}
        onClose={() => setShowMethodInfo(false)}
        title="Metodo de calculo de intereses"
        size="md"
      >
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Todos los creditos usan amortizacion francesa con convencion ACT/360 (dias reales / 360).
          </p>

          <div className="space-y-0">
            {[
              { icon: '1', text: 'Calcular dias del credito: semanas × 7' },
              { icon: '2', text: 'Calcular tasa del periodo: tasa_anual × dias / 360' },
              { icon: '3', text: 'Calcular tasa semanal: tasa_periodo / semanas' },
              { icon: '4', text: 'Aplicar formula francesa: cuota fija que incluye capital + interes' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/20 text-xs font-bold text-sky-400">
                    {step.icon}
                  </span>
                  {i < 3 && <div className="w-px flex-1 bg-border my-0.5" />}
                </div>
                <p className="pb-3 text-sm text-foreground">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 font-mono text-xs leading-relaxed text-sky-400">
            dias = semanas × 7<br />
            tasa_periodo = tasa_anual × dias / 360<br />
            r = tasa_periodo / semanas<br />
            <strong>cuota = P × r × (1+r)^N / ((1+r)^N - 1)</strong>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="mb-1 text-xs font-bold text-amber-400">$10,000 a 8 sem, 60%</p>
              <div className="space-y-0.5 text-[11px] text-amber-400/80">
                <p>Dias = 56, tasa_periodo = 9.33%</p>
                <p>Cuota semanal ≈ $1,350</p>
                <p className="font-bold">Total = $10,800 (intereses $800)</p>
              </div>
            </div>
            <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3">
              <p className="mb-1 text-xs font-bold text-blue-400">$10,000 a 12 sem, 65%</p>
              <div className="space-y-0.5 text-[11px] text-blue-400/80">
                <p>Dias = 84, tasa_periodo = 15.17%</p>
                <p>Cuota semanal ≈ $963</p>
                <p className="font-bold">Total = $11,556 (intereses $1,556)</p>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-muted/50 px-4 py-2.5">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Amortizacion francesa:</strong> cuota fija semanal. Al inicio la cuota tiene mas interes y menos capital. Al final, mas capital y menos interes. El cliente paga lo mismo cada semana.
            </p>
          </div>
        </div>
      </Modal>

      <ConfirmSaveModal
        isOpen={showConfirm}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
        isSaving={isSaving || isSavingFee}
      />
    </div>
  );
}
